import { useState } from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { t } from '@sportninja/common/i18n'
import {
  toAddExistingTeamPayload,
  toCreateTeamPayload,
  type RosterChoice,
} from '@sportninja/common/features/competition/operations/utils/addTeamPayload'

const translate = t as (key: string, options?: unknown) => string

interface Args {
  /** The division the team is attached to. */
  divisionScheduleId: string
  onAdded: () => void
}

export interface AddTeamToDivision {
  /** Attach a team that already exists, optionally cloning one of its rosters. */
  addExisting: (teamId: string, rosterChoice: RosterChoice) => Promise<boolean>
  /** Create a brand-new team by name; the backend also creates its roster. */
  createNew: (name: string) => Promise<boolean>
  isSaving: boolean
  error: string | null
}

/**
 * Both add-team paths.
 *
 * They share ONE endpoint — `POST /schedules/:divisionId/teams` — and the API
 * branches on whether the body carries `name` or `team_id`. Kept together so
 * that pairing stays obvious.
 */
export const useAddTeamToDivision = ({
  divisionScheduleId,
  onAdded,
}: Args): AddTeamToDivision => {
  'use memo'

  const { message } = App.useApp()
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const post = async (body: unknown, success: string): Promise<boolean> => {
    setIsSaving(true)
    setError(null)
    try {
      await req(`/schedules/${divisionScheduleId}/teams`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      message.success(success)
      onAdded()
      return true
    } catch (caught) {
      // `req` rejects with the parsed JSON body rather than an Error, so an
      // `instanceof` check would drop the API's reason for a hardcoded string.
      const text = getErrorMessage(caught)
      setError(text)
      message.error(text)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  return {
    // Both paths add a team to the division, so both use the creation wording.
    // There is no "x added" key in the bundles — inventing one would render the
    // raw key string on every tenant.
    addExisting: (teamId, rosterChoice) =>
      post(
        toAddExistingTeamPayload(teamId, rosterChoice),
        translate('Web:newXCreated', { x: translate('common:team') })
      ),
    createNew: (name) =>
      post(
        toCreateTeamPayload(name),
        translate('Web:newXCreated', { x: translate('common:team') })
      ),
    isSaving,
    error,
  }
}
